import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import './Header.css';

import { TablerSun, TablerMoon } from './Icons';
import { useTheme } from '../../contexts/ThemeContext';
import { useModule } from '../../contexts/ModuleContext';
import { ShieldCheck, HardHat, ChevronDown, Check } from 'lucide-react';
import { isBootstrapAdmin } from '../../utils/authStorage';
import { isFeatureEnabled } from '../../config/features';

const Header = ({ title = '', leftNode = null, rightNode = null, showLive = true, showTheme = true }) => {
  const { t } = useTranslation();
  const { isDarkMode, toggleTheme } = useTheme();
  const { activeModule, switchModule, canSwitchModules } = useModule();
  const navigate = useNavigate();
  const location = useLocation();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);


  const modules = [
    {
      id: 'surveillance',
      name: 'ASVI Surveillance',
      shortName: 'Surveillance',
      icon: ShieldCheck,
      path: '/dashboard',
    },
    {
      id: 'construction',
      name: 'ASVI Construction',
      shortName: 'Construction',
      icon: HardHat,
      path: '/construction/projects',
    },
  ];

  const currentModule = modules.find((m) => m.id === activeModule) || modules[0];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleModuleSelect = (mod) => {
    switchModule(mod.id);
    setDropdownOpen(false);
    // On the login screen only remember the vertical; navigate after auth.
    if (location.pathname !== '/login') {
      navigate(mod.path);
    }
  };

  return (
    <header className="app-top-bar">
      <div className="app-top-left">
        {leftNode && <div className="app-top-left-node">{leftNode}</div>}
        
        {/* Module Switcher Dropdown - only shown if user can switch domains */}
        {!isBootstrapAdmin() && canSwitchModules && (
          <div className="module-selector-container" ref={dropdownRef} style={{ position: 'relative', marginRight: '16px' }}>
            <button
              className="module-selector-btn"
              onClick={() => {
                if (canSwitchModules) setDropdownOpen((prev) => !prev);
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '6px 12px',
                borderRadius: '8px',
                backgroundColor: isDarkMode ? '#1E2330' : '#F3F4F6',
                border: `1px solid ${isDarkMode ? '#2E3545' : '#E5E7EB'}`,
                color: isDarkMode ? '#F3F4F6' : '#111827',
                fontSize: '13px',
                fontWeight: 600,
                cursor: canSwitchModules ? 'pointer' : 'default',
                transition: 'all 0.2s',
              }}
            >
              <currentModule.icon size={16} color={activeModule === 'construction' ? '#F59E0B' : '#0082F8'} />
              <span>{currentModule.shortName}</span>
              {canSwitchModules && (
                <ChevronDown size={14} style={{ opacity: 0.7, transform: dropdownOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
              )}
            </button>

            {canSwitchModules && dropdownOpen && (
              <div
                className="module-dropdown-menu"
                style={{
                  position: 'absolute',
                  top: 'calc(100% + 6px)',
                  left: 0,
                  width: '260px',
                  backgroundColor: isDarkMode ? '#161B26' : '#FFFFFF',
                  border: `1px solid ${isDarkMode ? '#2E3545' : '#E5E7EB'}`,
                  borderRadius: '10px',
                  boxShadow: isDarkMode ? '0 10px 25px rgba(0,0,0,0.5)' : '0 10px 25px rgba(0,0,0,0.1)',
                  padding: '6px',
                  zIndex: 1000,
                }}
              >
                <div style={{ padding: '6px 8px', fontSize: '11px', fontWeight: 600, color: isDarkMode ? '#9CA3AF' : '#6B7280', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Select Domain Intent
                </div>
                {modules.map((mod) => {
                  const Icon = mod.icon;
                  const isSelected = activeModule === mod.id;
                  return (
                    <button
                      key={mod.id}
                      onClick={() => handleModuleSelect(mod)}
                      style={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '10px 10px',
                        borderRadius: '6px',
                        backgroundColor: isSelected
                          ? (isDarkMode ? '#1F2937' : '#F3F4F6')
                          : 'transparent',
                        border: 'none',
                        color: isSelected
                          ? (isDarkMode ? '#F3F4F6' : '#111827')
                          : (isDarkMode ? '#9CA3AF' : '#4B5563'),
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontWeight: isSelected ? 600 : 400,
                        textAlign: 'left',
                        transition: 'background 0.15s',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Icon size={16} color={mod.id === 'construction' ? '#F59E0B' : '#0082F8'} />
                        <span>{mod.name}</span>
                      </div>
                      {isSelected && <Check size={14} color={mod.id === 'construction' ? '#F59E0B' : '#0082F8'} />}
                    </button>
                  );

                })}
              </div>
            )}
          </div>
        )}

        <h1 className="app-page-title">{title}</h1>

      </div>

      <div className="app-top-right">
        {showLive && isFeatureEnabled('HEADER_LIVE_BADGE') && (
          <div className="app-live-indicator">
            <span className="app-live-dot" />
            <span className="app-live-text">{t("status.live")}</span>
          </div>
        )}
        {rightNode}
        {showTheme && (
          <button className="app-theme-btn" onClick={toggleTheme} aria-label={t("ariaLabel.toggleTheme")}>
            {isDarkMode ? <TablerSun color="#E8EAED" size={18} /> : <TablerMoon color="#6B7280" size={18} />}
          </button>
        )}
      </div>
    </header>
  );
};

export default Header;